// Destinatário com busca automática: ao completar o CPF/CNPJ consulta o cadastro
// do cliente e, se for CNPJ novo, a Receita Federal; preenche tudo sozinho.
import React, { useEffect, useRef, useState } from "react";
import { fiscal } from "../api";
import { documentoValido, mascaraDocumento, soDigitos, UFS } from "../utils";
import { Campo, Entrada, Selecao } from "./ui";

const ENDERECO_VAZIO = {
  logradouro: "", numero: "", complemento: "", bairro: "", codigo_municipio: "", municipio: "", uf: "", cep: "",
};

export default function Destinatario({ valor, onChange, modelo = "55" }) {
  const d = valor || {};
  const [buscando, setBuscando] = useState(false);
  const [origem, setOrigem] = useState(null);
  const [erro, setErro] = useState(null);
  const ultimaBusca = useRef("");
  const nfce = modelo === "65";
  const nfse = modelo === "nfse";
  const emailObrigatorio = modelo === "55";

  const set = (campo, v) => onChange({ ...d, [campo]: v });
  const setEnd = (campo, v) => onChange({ ...d, endereco: { ...ENDERECO_VAZIO, ...(d.endereco || {}), [campo]: v } });

  useEffect(() => {
    const doc = soDigitos(d.documento);
    if (!documentoValido(doc) || doc === ultimaBusca.current) return;
    ultimaBusca.current = doc;
    let cancelado = false;
    setBuscando(true);
    setErro(null);
    fiscal
      .buscarDestinatario(doc)
      .then((r) => {
        if (cancelado) return;
        setOrigem(r.origem);
        if (r.origem !== "nao_encontrado") {
          onChange({
            ...d,
            ...r.destinatario,
            documento: doc,
            // e-mail digitado agora tem prioridade sobre o cadastrado
            email: d.email || r.destinatario.email || "",
            endereco: { ...ENDERECO_VAZIO, ...(r.destinatario.endereco || {}) },
          });
        }
      })
      .catch((e) => !cancelado && setErro(e.message))
      .finally(() => !cancelado && setBuscando(false));
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.documento]);

  const doc = soDigitos(d.documento);
  const docInvalido = doc.length >= 11 && !documentoValido(doc);
  const ehCpf = doc.length === 11;
  const end = d.endereco || ENDERECO_VAZIO;

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
      <Campo
        rotulo={nfce ? "CPF/CNPJ do consumidor (opcional)" : nfse ? "CPF/CNPJ do tomador" : "CPF/CNPJ"}
        className="md:col-span-2"
        erro={docInvalido ? "Documento inválido" : erro}
        dica={
          buscando
            ? "Consultando…"
            : origem === "cadastro"
            ? "Cliente já cadastrado — dados carregados"
            : origem === "receita"
            ? nfse ? "Dados preenchidos pela Receita Federal" : "Dados preenchidos pela Receita Federal — confira a IE"
            : origem === "nao_encontrado" && ehCpf
            ? "CPF novo: preencha os dados; ficam salvos após a primeira nota"
            : undefined
        }
      >
        <Entrada
          value={mascaraDocumento(d.documento)}
          onChange={(e) => set("documento", soDigitos(e.target.value))}
          placeholder="Digite o CPF ou CNPJ"
          inputMode="numeric"
        />
      </Campo>
      <Campo rotulo="Nome / Razão social" className="md:col-span-4">
        <Entrada value={d.nome || ""} onChange={(e) => set("nome", e.target.value)} maxLength={nfse ? 150 : 60} />
      </Campo>

      <Campo
        rotulo={emailObrigatorio ? "E-mail" : "E-mail (opcional)"}
        className="md:col-span-3"
        dica={nfce ? "Se informado, o XML e o DANFE são enviados por e-mail após a autorização"
          : nfse ? "Se informado, a NFS-e é enviada por e-mail após a autorização" : undefined}
      >
        <Entrada type="email" value={d.email || ""} onChange={(e) => set("email", e.target.value)} />
      </Campo>

      {nfse && (
        <Campo rotulo="Inscrição municipal (opcional)" className="md:col-span-3">
          <Entrada value={d.inscricao_municipal || ""} onChange={(e) => set("inscricao_municipal", e.target.value)} />
        </Campo>
      )}

      {!nfce && (
        <>
          {!nfse && (<>
          <Campo rotulo="Contribuinte de ICMS" className="md:col-span-2">
            <Selecao
              value={d.indicador_ie ?? 9}
              onChange={(e) => onChange({ ...d, indicador_ie: Number(e.target.value),
                // trocar a opção à mão desfaz a confirmação anterior
                ie_verificacao: d.ie_verificacao === "confirmado_usuario" ? null : d.ie_verificacao })}
              opcoes={[[1, "Contribuinte (com IE)"], [2, "Isento de IE"], [9, "Não contribuinte"]]}
            />
          </Campo>
          <Campo rotulo="Inscrição estadual" className="md:col-span-1">
            <Entrada
              value={d.inscricao_estadual || ""}
              disabled={Number(d.indicador_ie) !== 1}
              onChange={(e) => set("inscricao_estadual", e.target.value)}
            />
          </Campo>
          {!ehCpf && doc.length === 14 && <AvisoIe d={d} onChange={onChange} />}
          </>)}

          <Campo rotulo="CEP" className="md:col-span-1">
            <Entrada value={end.cep} onChange={(e) => setEnd("cep", soDigitos(e.target.value))} maxLength={8} />
          </Campo>
          <Campo rotulo="Logradouro" className="md:col-span-3">
            <Entrada value={end.logradouro} onChange={(e) => setEnd("logradouro", e.target.value)} />
          </Campo>
          <Campo rotulo="Número" className="md:col-span-1">
            <Entrada value={end.numero} onChange={(e) => setEnd("numero", e.target.value)} />
          </Campo>
          <Campo rotulo="Complemento" className="md:col-span-1">
            <Entrada value={end.complemento || ""} onChange={(e) => setEnd("complemento", e.target.value)} />
          </Campo>
          <Campo rotulo="Bairro" className="md:col-span-2">
            <Entrada value={end.bairro} onChange={(e) => setEnd("bairro", e.target.value)} />
          </Campo>
          <Campo rotulo="Município" className="md:col-span-2">
            <Entrada value={end.municipio} onChange={(e) => setEnd("municipio", e.target.value)} />
          </Campo>
          <Campo rotulo="Cód. IBGE" className="md:col-span-1">
            <Entrada value={end.codigo_municipio} onChange={(e) => setEnd("codigo_municipio", soDigitos(e.target.value))} maxLength={7} />
          </Campo>
          <Campo rotulo="UF" className="md:col-span-1">
            <Selecao value={end.uf} onChange={(e) => setEnd("uf", e.target.value)} vazio="--" opcoes={UFS.map((u) => [u, u])} />
          </Campo>
        </>
      )}
    </div>
  );
}


// Situação da inscrição estadual de uma EMPRESA destinatária (evita a rejeição 232 da SEFAZ)
function AvisoIe({ d, onChange }) {
  const uf = d.endereco?.uf || "";
  const ind = Number(d.indicador_ie ?? 9);
  if (ind === 1 && d.ie_verificacao === "sefaz_com_ie") {
    return <p className="md:col-span-6 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
      ✓ Inscrição estadual encontrada na SEFAZ{uf && `-${uf}`} e preenchida automaticamente. Confira.</p>;
  }
  if (ind === 1) {
    return !String(d.inscricao_estadual || "").replace(/\D/g, "")
      ? <p className="md:col-span-6 text-sm text-red-700">Informe a inscrição estadual do cliente.</p> : null;
  }
  if (ind === 9 && d.ie_verificacao === "sefaz_sem_ie") {
    return <p className="md:col-span-6 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
      SEFAZ{uf && `-${uf}`}: esta empresa não tem inscrição estadual ativa — segue como não contribuinte.</p>;
  }
  if (ind !== 9) return null;
  const confirmado = d.ie_verificacao === "confirmado_usuario";
  return (
    <div className="md:col-span-6 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <p className="font-medium">Esta empresa tem inscrição estadual?</p>
      <p>Não foi possível confirmar na SEFAZ{uf && `-${uf}`}. Se a empresa tiver IE, escolha <strong>“Contribuinte (com IE)”</strong> e
        informe o número — senão a SEFAZ rejeita a nota (232). A IE costuma estar no cartão CNPJ, na nota de compra do cliente ou com o contador.</p>
      <label className="mt-1 flex items-center gap-2">
        <input type="checkbox" checked={confirmado}
          onChange={(e) => onChange({ ...d, ie_verificacao: e.target.checked ? "confirmado_usuario" : null })} />
        Confirmo que esta empresa NÃO tem inscrição estadual
      </label>
    </div>
  );
}
